'use client';

import React from 'react';
import { DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ModalCloseButton } from '../common/modal-close-button';
import { CommonModal } from '../common/modal';

interface LogoutConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading?: boolean;
}

export function LogoutConfirmationModal({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
}: LogoutConfirmationModalProps) {
  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onOpenChange(false);
    }
  };

  // No `if (!open) return null` guard: CommonModal already renders nothing when closed, and
  // returning null first made `open` permanently true here — which suppressed the dialog's
  // exit animation.
  return (
    <CommonModal
      open={open}
      onOpenChange={onOpenChange}
      showCloseButton={false}
      className="sm:max-w-[420px]"
    >
      <div className="login-modal-container">
        <ModalCloseButton onClick={handleCancel} />
        <div className="login-modal-content">
          <DialogTitle className="cs-h3 tab:h1-compact mb-2">Confirm Logout</DialogTitle>
          <p className="cs-s-body-light text-text-default-medium mb-7.5">
            Are you sure you want to logout? You will need to login again to access your account.
          </p>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center">
            <Button
              onClick={handleCancel}
              variant="SecondaryFillButton"
              disabled={loading}
              className="cs-button w-full sm:w-auto sm:flex-1 touch-manipulation"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="PrimaryFillButton"
              disabled={loading}
              className="cs-button w-full sm:w-auto sm:flex-1 touch-manipulation"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Logging out...
                </span>
              ) : (
                'Logout'
              )}
            </Button>
          </div>
        </div>
      </div>
    </CommonModal>
  );
}

